import { useState, useEffect, useCallback } from 'react';

export interface MonitorStatus {
  name: string;
  status: 'online' | 'offline' | 'unknown';
}

export interface UptimeStatus {
  onlineCount: number;
  offlineCount: number;
  offlineSites: string[];
  monitors: MonitorStatus[];
  isLoading: boolean;
  errorMessage: string | null;
}

export const useUptimeKuma = () => {
  const [status, setStatus] = useState<UptimeStatus>({
    onlineCount: 0,
    offlineCount: 0,
    offlineSites: [],
    monitors: [],
    isLoading: false,
    errorMessage: null,
  });

  // ✅ BEZPIECZNE - API key z zmiennych środowiskowych
  const API_KEY = import.meta.env.VITE_UPTIME_KUMA_API_KEY || '';
  const API_URL = "/api/uptime-kuma/metrics";

  const getMonitorsStatus = useCallback(async () => {
    setStatus(prev => ({ ...prev, isLoading: true }));

    // ✅ BEZPIECZNE - walidacja API key
    if (!API_KEY) {
      setStatus(prev => ({ 
        ...prev, 
        isLoading: false, 
        errorMessage: 'Uptime Kuma API key not configured' 
      }));
      return;
    }

    try {
      console.log('[useUptimeKuma] Fetching monitor status...');

      // Create basic auth header - Uptime Kuma uses username:password format
      // For API keys, username is empty, password is the API key
      const authString = `:${API_KEY}`;
      const authBytes = btoa(authString);

      console.log('[useUptimeKuma] Auth string created, making request to:', API_URL);

      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          "Authorization": `Basic ${authBytes}`,
          "Content-Type": "text/plain",
        },
        mode: 'cors', // Try CORS mode
        signal: AbortSignal.timeout(30000), // 30 second timeout
      });

      console.log('[useUptimeKuma] Response status:', response.status);
      console.log('[useUptimeKuma] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[useUptimeKuma] API Error response:', errorText);
        throw new Error(`API Error ${response.status}: ${errorText}`);
      }

      const metrics = await response.text();
      console.log('[useUptimeKuma] Raw metrics response length:', metrics.length);
      // ✅ BEZPIECZNE - nie logujemy pełnej odpowiedzi (może zawierać wrażliwe dane)
      console.log('[useUptimeKuma] Response received:', !!metrics);

      const lines = metrics.split('\n');
      const statusLines = lines.filter(line => line.startsWith('monitor_status{'));

      console.log('[useUptimeKuma] Found', statusLines.length, 'monitor status lines');

      let onlineCount = 0;
      let offlineCount = 0;
      const offlineSites: string[] = [];
      const monitors: MonitorStatus[] = [];

      for (const line of statusLines) {
        console.log('[useUptimeKuma] Processing line:', line);

        // Parse monitor name from line like: monitor_status{monitor_name="example.com",monitor_type="http"} 1
        const nameMatch = line.match(/monitor_name="([^"]+)"/);
        if (!nameMatch) {
          console.log('[useUptimeKuma] No monitor name found in line:', line);
          continue;
        }

        const monitorName = nameMatch[1];
        const isOnline = line.trim().endsWith('} 1');

        console.log('[useUptimeKuma] Monitor:', monitorName, 'isOnline:', isOnline);

        const monitorStatus: MonitorStatus = {
          name: monitorName,
          status: isOnline ? 'online' : 'offline'
        };

        monitors.push(monitorStatus);

        if (isOnline) {
          onlineCount++;
        } else {
          offlineCount++;
          offlineSites.push(monitorName);
        }
      }

      console.log('[useUptimeKuma] Final counts - Online:', onlineCount, 'Offline:', offlineCount);

      setStatus({
        onlineCount,
        offlineCount,
        offlineSites,
        monitors,
        isLoading: false,
        errorMessage: null,
      });

    } catch (error) {
      console.error('[useUptimeKuma] Error fetching monitor status:', error);

      let errorMessage = 'Unknown error';
      if (error instanceof Error) {
        errorMessage = error.message;
        if (error.name === 'AbortError') {
          errorMessage = 'Request timeout - API nie odpowiada';
        }
      }

      setStatus(prev => ({
        ...prev,
        isLoading: false,
        errorMessage,
      }));
    }
  }, [API_KEY, API_URL]);

  // Auto-refresh every 2 minutes
  useEffect(() => {
    getMonitorsStatus(); // Initial load

    const interval = setInterval(() => {
      getMonitorsStatus();
    }, 2 * 60 * 1000); // 2 minutes

    return () => clearInterval(interval);
  }, [getMonitorsStatus]);

  // Function to check status of specific monitor
  const getMonitorStatus = useCallback((monitorName: string): 'online' | 'offline' | 'unknown' => {
    const monitor = status.monitors.find(m => m.name === monitorName);
    return monitor?.status || 'unknown';
  }, [status.monitors]);

  return {
    ...status,
    getMonitorsStatus,
    getMonitorStatus,
  };
};
